import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { TickCircle } from "iconsax-reactjs";
import { Circle } from "lucide-react";
import AppModal from "../AppModal";
import { safeCurrencyFormat } from "../../utilities/safeCurrencyFormat";
import { getOtcTransactionState } from "../../utilities/transactionUtils";

const ACTION_LABELS = {
    requested: "You submitted your OTC request",
    processing: "Your OTC request is being reviewed",
    request_approved: "Your requested rate was approved",
    offer_sent: "We’ve sent an offer",
    offer_accepted: "You accepted the offer",
    awaiting_funding: "We’re waiting to confirm your transfer",
    transfer_details_submitted: "Recipient details submitted",
    external_settlement_initiated: "Your transfer is being processed",
    completed: "You completed the OTC transfer",
    offer_declined: "You declined our offer",
    offer_expired: "Offer expired before you accepted",
    request_rejected: "We’ve rejected your request",
    cancelled: "You cancelled your OTC request",
};

const isClosedAction = (item = {}) =>
    /rejected|declined|expired|cancelled|canceled/i.test(`${item.action} ${item.status}`);

const formatCountdown = (expiresAt, now) => {
    if (!expiresAt) return "";
    const remaining = Math.max(0, dayjs(expiresAt).diff(now, "second"));
    if (remaining <= 0) return "";
    const hours = String(Math.floor(remaining / 3600)).padStart(2, "0");
    const minutes = String(Math.floor((remaining % 3600) / 60)).padStart(2, "0");
    const seconds = String(remaining % 60).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
};

const DetailRow = ({ label, children, valueClassName = "" }) => (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] gap-4 py-2">
        <span className="text-[13px] text-[#6F739B]">{label}</span>
        <div className={`text-[13px] text-[#101828] text-right break-words ${valueClassName}`}>
            {children || "—"}
        </div>
    </div>
);

const Receiver = ({ otc }) => {
    if (!otc.receiverName && !otc.accountNumber && !otc.bankName) return "After approval";
    return (
        <div className="flex flex-col items-end gap-0.5">
            <span>{otc.receiverName || "—"}</span>
            {otc.bankName && <span className="text-[#6F739B]">{otc.bankName}</span>}
            {otc.accountNumber && <span className="text-[#6F739B]">{otc.accountNumber}</span>}
        </div>
    );
};

export default function OTCTransactionDetails({
    txn,
    footer,
    onOtcAction,
    onAcceptOffer,
    onCompleteOffer,
    onExpire,
}) {
    const [activeTab, setActiveTab] = useState("updates");
    const [confirmAction, setConfirmAction] = useState(null);
    const [reason, setReason] = useState("");
    const [isSubmittingAction, setIsSubmittingAction] = useState(false);
    const [now, setNow] = useState(dayjs());
    const hasTriggeredExpire = useRef(false);

    const rawOtc = txn?.otc || {};
    const isOfferExpired = Boolean(
        rawOtc.expiresAt &&
            !rawOtc.acceptedAt &&
            !["rejected", "declined", "cancelled", "completed"].includes(txn?.otcState?.phase) &&
            dayjs(rawOtc.expiresAt).diff(now, "second") <= 0
    );

    const activeTxn = useMemo(() => {
        if (!isOfferExpired || txn?.otcState?.phase === "expired") {
            return txn || {};
        }
        const nextOtc = { ...rawOtc };
        const history = [...(rawOtc.history || [])];
        if (!history.some((item) => /offer_expired|expired/i.test(item.action || item.status))) {
            history.unshift({
                action: "offer_expired",
                status: "expired",
                at: rawOtc.expiresAt,
                message: "Offer expired before you accepted",
            });
        }
        nextOtc.history = history;
        const simulatedTxn = { ...txn, otc: nextOtc };
        const otcState = getOtcTransactionState(simulatedTxn);
        return {
            ...simulatedTxn,
            otcState,
            status: otcState.displayStatus,
        };
    }, [txn, rawOtc, isOfferExpired]);

    const otc = activeTxn.otc || rawOtc;
    const state = activeTxn.otcState || {};

    const txnIdentifier = txn?.id || txn?._id || rawOtc.expiresAt;
    useEffect(() => {
        hasTriggeredExpire.current = false;
    }, [txnIdentifier]);

    useEffect(() => {
        if (isOfferExpired && txn?.otcState?.phase !== "expired" && !hasTriggeredExpire.current) {
            hasTriggeredExpire.current = true;
            onExpire?.();
        }
    }, [isOfferExpired, txn?.otcState?.phase, onExpire]);

    useEffect(() => {
        if (!rawOtc.expiresAt) return undefined;
        const timer = window.setInterval(() => setNow(dayjs()), 1000);
        return () => window.clearInterval(timer);
    }, [rawOtc.expiresAt]);

    const countdown = formatCountdown(otc.expiresAt, now);
    const offeredLabel = state.phase === "completed" ? "Agreed rate" : "Our offered rate";
    const detailRows = (() => {
        const rows = [];
        if (otc.offeredRate != null) {
            rows.push({
                label: offeredLabel,
                value: (
                    <span className="inline-flex items-center justify-end gap-2">
                        {state.canRespondToOffer && countdown && (
                            <span className="text-[#6F00FF]">{countdown}</span>
                        )}
                        <span className="font-medium">{otc.offeredRateText}</span>
                    </span>
                ),
            });
        }
        if (otc.preferredRate != null) {
            rows.push({ label: "Your preferred rate", value: otc.preferredRateText });
        }
        rows.push(
            { label: "Recipient receives", value: otc.recipientAmountText },
            { label: "Standard rate", value: otc.standardRateText },
            { label: "Note", value: otc.note || "—" },
            { label: "Fee", value: otc.feeText },
            { label: "Currency pair", value: otc.currencyPair },
            { label: "Type", value: "OTC Transfer" },
            { label: "Reference", value: otc.reference },
            { label: "Receiver", value: <Receiver otc={otc} /> }
        );
        return rows;
    })();

    const actionButtons = (
        <div className="flex items-center justify-end gap-2">
            {state.canCancel && (
                <button
                    type="button"
                    className="text-[12px] text-[#475467] px-3 py-2 rounded-md hover:bg-[#F7F8FD]"
                    onClick={() => setConfirmAction("cancel")}
                >
                    Cancel request
                </button>
            )}
            {state.canRespondToOffer && (
                <>
                    <button
                        type="button"
                        className="text-[12px] text-[#475467] px-3 py-2 rounded-md hover:bg-[#F7F8FD]"
                        onClick={() => setConfirmAction("decline")}
                    >
                        Decline
                    </button>
                    {activeTab === "details" && (
                        <button
                            type="button"
                            disabled={!onAcceptOffer}
                            className="text-[12px] text-white bg-[#6F00FF] px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => onAcceptOffer?.(activeTxn)}
                        >
                            Accept
                        </button>
                    )}
                </>
            )}
            {state.canComplete && activeTab === "details" && (
                <button
                    type="button"
                    disabled={!onCompleteOffer}
                    className="text-[12px] text-white bg-[#6F00FF] px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => onCompleteOffer?.(activeTxn)}
                >
                    Complete
                </button>
            )}
        </div>
    );

    const confirmCopy =
        confirmAction === "decline"
            ? {
                  title: "Decline this offer?",
                  body: "This will close your request. You’ll need to submit a new one to apply again.",
                  label: "Decline offer",
              }
            : {
                  title: "Cancel this request?",
                  body: "You’ll lose any progress and updates on this request. You’ll need to start a new request to submit again.",
                  label: "Cancel request",
              };

    return (
        <>
            <div className="flex flex-col p-5 bg-white h-full min-h-[640px] overflow-y-auto">
                <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 shrink-0 bg-[#F2F3FC] rounded-full grid place-items-center text-[#383B56] text-[13px] font-medium">
                            {(activeTxn.receiverName || activeTxn.title || "OT")
                                .split(/\s+/)
                                .filter(Boolean)
                                .slice(0, 2)
                                .map((word) => word[0])
                                .join("")
                                .toUpperCase()}
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-[14px] font-medium text-[#101828] truncate mb-0">
                                {activeTxn.title}
                            </h2>
                            <p className="text-[11px] text-[#6F739B] mb-0">
                                {dayjs(activeTxn.time).format("MMM Do, YYYY • h:mm A")}
                            </p>
                        </div>
                    </div>
                    <div className="shrink-0 text-right">
                        <p className="text-[17px] font-semibold text-[#101828] mb-0">
                            {safeCurrencyFormat(Math.abs(activeTxn.amount), activeTxn.currency)}
                        </p>
                        <p
                            className={`text-[11px] font-medium mb-0 ${
                                activeTxn.status === "Completed"
                                    ? "text-[#188B18]"
                                    : activeTxn.status === "Closed"
                                    ? "text-[#D92D20]"
                                    : "text-[#E66B00]"
                            }`}
                        >
                            {activeTxn.status}
                        </p>
                    </div>
                </div>

                <div
                    className="flex min-h-0 flex-1 flex-col rounded-lg"
                    data-feature-tour="otc-request-details"
                >
                    <div className="flex items-end justify-between border-b border-[#EAECF0] bg-white">
                        <div className="flex gap-1">
                            {["updates", "details"].map((tab) => (
                                <button
                                    key={tab}
                                    type="button"
                                    onClick={() => setActiveTab(tab)}
                                    className={`capitalize text-[12px] px-3 py-2 border-b-2 ${
                                        activeTab === tab
                                            ? "border-[#6F00FF] text-[#101828]"
                                            : "border-transparent text-[#667085]"
                                    }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                        <div className="pb-1">{actionButtons}</div>
                    </div>

                    {activeTab === "updates" ? (
                        <div className="bg-[#F7F8FD] rounded-lg p-4 mt-0 flex-1 min-h-[330px]">
                            <ol className="space-y-0">
                                {(otc.history || []).map((item, index) => {
                                    const isCurrent = index === 0;
                                    const isClosed = isClosedAction(item);
                                    const label = item.message || ACTION_LABELS[item.action] || item.action;
                                    return (
                                        <li key={`${item.action}-${item.at}-${index}`} className="relative flex gap-3 pb-5 last:pb-0">
                                            {index < otc.history.length - 1 && (
                                                <span className="absolute left-[7px] top-5 bottom-0 w-px bg-[#E4E7F5]" />
                                            )}
                                            <span className="relative z-[1] mt-0.5 bg-[#F7F8FD]">
                                                {isCurrent ? (
                                                    <Circle
                                                        size={15}
                                                        strokeWidth={3}
                                                        className={isClosed ? "text-[#D92D20]" : "text-[#11A811]"}
                                                    />
                                                ) : (
                                                    <TickCircle size="15" color="#11A811" variant="Bold" />
                                                )}
                                            </span>
                                            <div className="min-w-0">
                                                <p className={`text-[12px] mb-0 ${isCurrent ? "font-medium text-[#101828]" : "text-[#475467]"}`}>
                                                    {label}
                                                </p>
                                                {item.at && (
                                                    <p className="text-[11px] text-[#6F739B] mt-1 mb-0">
                                                        {dayjs(item.at).format("MMM D, YYYY • h:mm A")}
                                                    </p>
                                                )}
                                                {item.expiresAt && state.canRespondToOffer && countdown && (
                                                    <p className="text-[11px] text-[#6F739B] mt-1 mb-0">
                                                        This offer expires in <span className="text-[#6F00FF]">{countdown}</span>.
                                                    </p>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>
                    ) : (
                        <div className="bg-[#F7F8FD] rounded-lg px-4 py-2 mt-0 flex-1">
                            {detailRows.map((row) => (
                                <DetailRow key={row.label} label={row.label}>
                                    {row.value}
                                </DetailRow>
                            ))}
                        </div>
                    )}
                </div>

                {footer}
            </div>

            <AppModal
                isOpen={Boolean(confirmAction)}
                onClose={() => setConfirmAction(null)}
                title={confirmCopy.title}
                className="!max-w-[560px]"
                titleClassName="!text-left pr-10"
                disableOutsideClose={isSubmittingAction}
                body={
                    <div className="text-left">
                        <p className="text-[#475467] mb-0">{confirmCopy.body}</p>
                        {confirmAction === "decline" && (
                            <label className="block mt-7 text-[#101828]">
                                Reason <span className="italic text-[#475467]">• Optional</span>
                                <textarea
                                    value={reason}
                                    onChange={(event) => setReason(event.target.value)}
                                    placeholder="Why are you declining this offer?"
                                    className="mt-2 w-full h-24 resize-none bg-[#F2F3FC] rounded-lg p-4 outline-none focus:ring-2 focus:ring-[#CBB8FF]"
                                />
                            </label>
                        )}
                    </div>
                }
                secondary={{ label: "Go back", onClick: () => setConfirmAction(null) }}
                primary={{
                    label: confirmCopy.label,
                    variant: "danger",
                    disabled: !onOtcAction,
                    loading: isSubmittingAction,
                    onClick: async () => {
                        if (!onOtcAction) return;
                        setIsSubmittingAction(true);
                        try {
                            const succeeded = await onOtcAction(confirmAction, activeTxn, reason);
                            if (succeeded !== false) {
                                setConfirmAction(null);
                                setReason("");
                            }
                        } finally {
                            setIsSubmittingAction(false);
                        }
                    },
                }}
            />
        </>
    );
}
